import { flightsApi } from './flights.api';
import { hotelsApi } from './hotels.api';
import { bookingsApi } from './bookings.api';
import { tipsApi } from './tips.api';
import { expensesApi } from './expenses.api';
import { notesApi } from './notes.api';
import type { BookingType, ContentSource, ExpenseCategory, Expense } from '../types';
import type {
  FlightProposal, HotelProposal, BookingProposal, TipProposal, PackingProposal, ExpenseProposal,
} from './import.api';

// Persist glue for the extra categories a document import can produce (tickets,
// tips, packing, expenses). Venues + day-plans reuse api/aiProposals.ts.

/** The document's own remarks, kept alongside whatever the ticket itself carries. */
function joinNotes(...parts: (string | null | undefined)[]): string | null {
  const kept = parts.map(p => p?.trim()).filter((p): p is string => !!p);
  return kept.length > 0 ? Array.from(new Set(kept)).join('\n') : null;
}

export async function persistFlightProposal(tripId: string, f: FlightProposal, source: ContentSource = 'IMPORT'): Promise<void> {
  const first = f.legs[0];
  const last  = f.legs[f.legs.length - 1];
  await flightsApi.create(tripId, {
    direction:        f.direction ?? 'OUTBOUND',
    // Flights have no bookingUrl column, so a booking link found in the document
    // is kept in the notes rather than dropped.
    notes:            joinNotes(f.bookingRef ? `PNR: ${f.bookingRef}` : null, f.notes, f.bookingUrl),
    departureAirport: first.fromAirport.toUpperCase(),
    arrivalAirport:   last.toAirport.toUpperCase(),
    departureTime:    first.departureTime,
    arrivalTime:      last.arrivalTime,
    flightNumber:     first.flightNumber ?? null,
    airline:          first.airline ?? null,
    source,
    stops: f.legs.map(l => ({
      fromAirport:   l.fromAirport.toUpperCase(),
      toAirport:     l.toAirport.toUpperCase(),
      departureTime: l.departureTime,
      arrivalTime:   l.arrivalTime,
      flightNumber:  l.flightNumber ?? null,
      airline:       l.airline ?? null,
    })),
  });
}

export async function persistHotelProposal(tripId: string, h: HotelProposal, source: ContentSource = 'IMPORT'): Promise<void> {
  await hotelsApi.create(tripId, {
    name:       h.name,
    address:    h.address ?? null,
    checkIn:    h.checkIn,
    checkOut:   h.checkOut,
    bookingRef: h.bookingRef ?? null,
    bookingUrl: h.bookingUrl ?? null,
    notes:      joinNotes(h.notes),
    source,
  });
}

export async function persistBookingProposal(tripId: string, b: BookingProposal, source: ContentSource = 'IMPORT'): Promise<void> {
  await bookingsApi.create(tripId, {
    type:       (b.type as BookingType) ?? 'OTHER',
    title:      b.title,
    date:       b.date,
    startTime:  b.startTime ?? null,
    endTime:    b.endTime ?? null,
    venue:      b.venue ?? null,
    address:    b.address ?? null,
    bookingRef: b.bookingRef ?? null,
    bookingUrl: b.bookingUrl ?? null,
    notes:      joinNotes(b.notes),
    source,
  });
}

// Keeps the raw uploaded files (the AI only reads them once, then discards them)
// in the trip's document manager — the Notes tab — grouped under a single reused
// note so the original source is never lost after an import. Best-effort: a
// storage failure must not fail the import that already succeeded, so callers
// swallow the rejection. Files are re-sent from the browser because extraction
// used memory storage and never persisted them.
export async function saveImportedDocuments(tripId: string, files: File[], noteTitle: string): Promise<void> {
  if (files.length === 0) return;
  const existing = await notesApi.list(tripId);
  const note = existing.find(n => n.title.trim() === noteTitle.trim())
    ?? await notesApi.create(tripId, { title: noteTitle, content: '' });
  for (const file of files) {
    await notesApi.uploadDocument(tripId, note.id, file);
  }
}

export async function persistTipProposal(tripId: string, t: TipProposal): Promise<void> {
  await tipsApi.create(tripId, { category: t.category, content: t.content, source: 'IMPORT' });
}

export async function persistExpenseProposal(tripId: string, e: ExpenseProposal): Promise<void> {
  // Same cast the ExpenseForm uses — `Partial<Expense>` types splits as the full
  // persisted shape (id/expenseId), which a create payload doesn't have yet.
  const payload = {
    description:   e.description,
    category:      e.category as ExpenseCategory,
    amount:        e.amount,
    currency:      e.currency,
    paymentMethod: e.paymentMethod,
    status:        e.status,
    date:          e.date,
    notes:         e.notes ?? null,
    source:        'IMPORT',
    splits:        e.splits,
  } as Partial<Expense> & { splits: { userId: string; amount: number }[] };
  await expensesApi.create(tripId, payload);
}

// Packing needs section resolution shared across items, so it's persisted as a
// group: reuse an existing checklist section with the same title, else create it.
export async function persistPackingProposals(tripId: string, groups: PackingProposal[]): Promise<void> {
  if (groups.length === 0) return;
  const existing = await tipsApi.listChecklists(tripId);
  // Only the trip's shared sections are candidates to merge into. An imported
  // document describes the trip, not one traveller's suitcase, and the list now
  // also contains sections belonging to other people — matching a title against
  // one of those would try to write into someone else's list and be refused.
  const byTitle = new Map(
    existing.filter(s => s.ownerUserId === null).map(s => [s.title.trim().toLowerCase(), s.id]),
  );
  for (const g of groups) {
    const key = g.section.trim().toLowerCase();
    let sectionId = byTitle.get(key);
    if (!sectionId) {
      const created = await tipsApi.createSection(tripId, { title: g.section, source: 'IMPORT', scope: 'common' });
      sectionId = created.id;
      byTitle.set(key, sectionId);
    }
    for (const item of g.items) {
      await tipsApi.createItem(tripId, sectionId, { text: item });
    }
  }
}
